import { useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";

/**
 * Thin top-of-page progress bar that animates during route transitions.
 * Uses router state so it works for every <Link> navigation app-wide.
 */
export function RouteProgressBar() {
  const isLoading = useRouterState({ select: (s) => s.isLoading || s.status === "pending" });
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let trickle: ReturnType<typeof setInterval> | undefined;
    let hide: ReturnType<typeof setTimeout> | undefined;

    if (isLoading) {
      setVisible(true);
      setProgress(15);
      trickle = setInterval(() => {
        setProgress((p) => (p < 85 ? p + Math.random() * 8 : p));
      }, 180);
    } else if (visible) {
      setProgress(100);
      hide = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 280);
    }

    return () => {
      if (trickle) clearInterval(trickle);
      if (hide) clearTimeout(hide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-[3px]"
      style={{ opacity: visible ? 1 : 0, transition: "opacity 240ms ease-out" }}
    >
      <div
        className="h-full bg-gradient-gold shadow-gold"
        style={{
          width: `${progress}%`,
          transition: "width 240ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />
    </div>
  );
}
